import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { fetchCurrentUser } from "../utils/api/api";

const AuthUserContext = createContext(null);
const STORAGE_KEY = "vxpro_current_user";

export const AuthUserProvider = ({ children }) => {
  const [user, setUserState] = useState(null);
  const [loading, setLoading] = useState(true);
  const hasRestoredRef = useRef(false);

  // Restore user from localStorage on mount, then validate token
  useEffect(() => {
    let cancelled = false;

    const restore = async () => {
      let restored = null;
      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (raw) restored = JSON.parse(raw);
      } catch {
        // ignore JSON errors
      }

      if (!restored?.token) {
        // Nothing stored or no token — stay signed out
        hasRestoredRef.current = true;
        setLoading(false);
        return;
      }

      // Optimistically set user so the UI doesn't flash login
      setUserState(restored);

      try {
        const data = await fetchCurrentUser(restored.token);
        if (!cancelled) {
          // Merge any fresh fields (e.g. isAdmin) from the server
          setUserState((prev) => ({ ...(prev || {}), ...data }));
        }
      } catch (err) {
        if (!cancelled && (err.status === 401 || err.status === 403)) {
          // Token is invalid/expired — clear and send to login
          setUserState(null);
          localStorage.removeItem(STORAGE_KEY);
        }
      } finally {
        if (!cancelled) {
          hasRestoredRef.current = true;
          setLoading(false);
        }
      }
    };

    restore();
    return () => { cancelled = true; };
  }, []);

  // Persist user to localStorage when it changes (but not on initial mount)
  useEffect(() => {
    // Don't persist until we've restored from localStorage
    if (!hasRestoredRef.current) return;

    if (user) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(user));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, [user]);

  const signIn = useCallback((payload) => {
    setUserState((prev) => ({
      ...(prev || {}),
      ...payload,
      onboarding: payload.onboarding ?? prev?.onboarding ?? null,
    }));
  }, []);

  const signOut = useCallback(() => {
    setUserState(null);
  }, []);

  const refreshUser = useCallback(async (tokenOverride = null) => {
    const token =
      tokenOverride ||
      JSON.parse(localStorage.getItem(STORAGE_KEY) || "null")?.token;
    if (!token) return;
    try {
      const data = await fetchCurrentUser(token);
      setUserState((prev) => ({ ...(prev || {}), ...data }));
    } catch {
      // non-fatal — stale data is acceptable here
    }
  }, []);

  return (
    <AuthUserContext.Provider
      value={{ user, setUser: setUserState, signIn, signOut, loading, refreshUser }}
    >
      {children}
    </AuthUserContext.Provider>
  );
};

export const useAuthUser = () => useContext(AuthUserContext);
